'use client';

import type { IProductListItem } from '@/api/types/product';
import type { Product } from '@/components/Product/ProductCard';
import { env } from '@/utils/env';
import { EProductImageType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import type { MarketingRankingProductCardProps } from './MarketingRankingProductCard';

export const MARKETING_BRAND_PRODUCTS_SECTION_ID = 'marketing-brand-products-section';

export function withS3Url(path?: string): string {
  const normalizedPath = typeof path === 'string' ? path.trim() : '';
  if (!normalizedPath) {
    return '';
  }
  if (/^(https?:)?\/\//.test(normalizedPath)) {
    return normalizedPath;
  }
  return `${env.NEXT_PUBLIC_S3_URL}${normalizedPath}`;
}

export type MarketingProductItem = IProductListItem & Record<string, unknown>;
export type MarketingFlashSaleProductItem = MarketingProductItem;

const DEFAULT_MARKETING_PRODUCT_HOVER_IMAGE_RULE = [
  EProductImageType.MATERIAL,
  EProductImageType.CELEBRITY,
  EProductImageType.PRODUCT,
] as const;
const MARKETING_PRODUCT_IMAGE_RULE_TYPES = new Set<number>([
  EProductImageType.PRODUCT,
  EProductImageType.CELEBRITY,
  EProductImageType.MATERIAL,
]);

export function getStringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function getNumberLikeString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return getStringValue(value);
}

function getFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function getImagePath(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      getImagePath(record.url) ||
      getImagePath(record.path) ||
      getImagePath(record.src) ||
      getImagePath(record.image_url)
    );
  }

  return '';
}

function getImageGroupPath(image: unknown, groupName: string, index: number) {
  if (!image || typeof image !== 'object') {
    return '';
  }

  const group = (image as Record<string, unknown>)[groupName];
  if (!Array.isArray(group)) {
    return '';
  }

  return getImagePath(group[index]);
}

export function normalizeMarketingProductImageRule(imageRule?: unknown[]) {
  if (!Array.isArray(imageRule)) {
    return undefined;
  }

  return imageRule
    .map((type) => Number(type))
    .filter((type): type is EProductImageType => MARKETING_PRODUCT_IMAGE_RULE_TYPES.has(type));
}

export function getMarketingFlashSaleProductImage(product: MarketingFlashSaleProductItem) {
  const image = product.image;

  if (Array.isArray(image?.product) && image.product[0]) {
    return withS3Url(image.product[0]);
  }

  if (typeof image === 'string') {
    return withS3Url(image);
  }

  return (
    withS3Url(getStringValue(product.img_url)) ||
    withS3Url(getStringValue(product.main_image)) ||
    withS3Url(getStringValue(product.product_image)) ||
    withS3Url(getStringValue(product.image_url))
  );
}

export function getMarketingProductHoverImage(
  product: MarketingProductItem,
  imageHoverRule?: unknown[],
) {
  const image = product.image;
  const resolvedImageHoverRule =
    normalizeMarketingProductImageRule(imageHoverRule) ||
    DEFAULT_MARKETING_PRODUCT_HOVER_IMAGE_RULE;

  for (const type of resolvedImageHoverRule) {
    if (type === EProductImageType.MATERIAL) {
      const materialImage = withS3Url(getImageGroupPath(image, 'material', 0));
      if (materialImage) {
        return materialImage;
      }
    }

    if (type === EProductImageType.CELEBRITY) {
      const celebrityImage = withS3Url(getImageGroupPath(image, 'celebrity', 0));
      if (celebrityImage) {
        return celebrityImage;
      }
    }

    if (type === EProductImageType.PRODUCT) {
      const productImage =
        withS3Url(getImageGroupPath(image, 'product', 1)) ||
        withS3Url(getImageGroupPath(image, 'product_gray', 0));

      if (productImage) {
        return productImage;
      }
    }
  }

  return '';
}

function getMarketingProductCardImage(product: MarketingProductItem, image: string) {
  const sourceImage = product.image;
  let productImage: unknown = { product: [] };
  if (image && typeof sourceImage === 'object' && sourceImage !== null) {
    const productImages = Array.isArray(sourceImage.product) ? sourceImage.product : [];
    productImage = { ...sourceImage, product: [image, ...productImages.slice(1)] };
  } else if (image) {
    productImage = { product: [image] };
  } else if (typeof sourceImage === 'object' && sourceImage !== null) {
    productImage = sourceImage;
  }

  return productImage;
}

export function getMarketingProductCardProduct(
  product: MarketingProductItem,
  image: string,
): Product {
  const productImage = getMarketingProductCardImage(product, image);
  const correlation = Array.isArray(product.correlation)
    ? product.correlation.map((item) => {
        const correlationProduct = item as unknown as MarketingProductItem;
        return {
          ...item,
          image: getMarketingProductCardImage(
            correlationProduct,
            getMarketingFlashSaleProductImage(correlationProduct),
          ),
        };
      })
    : product.correlation;
  const price = getNumberLikeString(product.price) || getNumberLikeString(product.original_price);
  const sellPrice =
    getNumberLikeString(product.sell_price) || getNumberLikeString(product.sale_price) || price;
  const shopSku =
    getStringValue(product.shop_sku) ||
    getStringValue(product.sku) ||
    getStringValue(product.business_sku);
  const name = getMarketingFlashSaleProductName(product);
  const stock = getFiniteNumber(product.stock);
  const status = getFiniteNumber(product.status);

  return {
    ...product,
    id: product.id ?? shopSku,
    name,
    short_name: getStringValue(product.short_name) || name,
    shop_sku: shopSku,
    business_sku: getStringValue(product.business_sku) || shopSku,
    image: productImage,
    correlation,
    price_info:
      Array.isArray(product.price_info) && product.price_info.length
        ? product.price_info
        : sellPrice
          ? [
              {
                currency: 'USD',
                price,
                sell_price: sellPrice,
              },
            ]
          : [],
    is_glass: Number(product.is_glass) || 1,
    is_wish: Number(product.is_wish) || 2,
    wish_num: Number(product.wish_num) || 0,
    status,
    stock,
  } as unknown as Product;
}

export function getMarketingFlashSaleProductName(product: MarketingFlashSaleProductItem) {
  return (
    product.short_name ||
    product.name ||
    getStringValue(product.product_name) ||
    product.shop_sku ||
    ''
  );
}

export function getMarketingFlashSaleProductHref(product: MarketingFlashSaleProductItem) {
  const configuredUrl =
    getStringValue(product.url) ||
    getStringValue(product.href) ||
    getStringValue(product.product_url) ||
    getStringValue(product.jump_url) ||
    getStringValue(product.seo_url);
  if (configuredUrl) {
    return configuredUrl;
  }

  const sku =
    getStringValue(product.shop_sku) ||
    getStringValue(product.business_sku) ||
    getStringValue(product.sku);

  return sku ? `/goods-detail/${sku}` : '';
}

export function getMarketingFlashSaleDisplayPrice(
  product: MarketingFlashSaleProductItem,
  currency: unknown,
  userInfo: unknown,
) {
  const storePrice = priceInfo(userInfo, product, currency) as {
    price?: string;
    sell_price?: string;
  };

  const directSellPrice =
    getNumberLikeString(storePrice?.sell_price) ||
    getNumberLikeString(storePrice?.price) ||
    getNumberLikeString(product.sell_price) ||
    getNumberLikeString(product.sale_price) ||
    getNumberLikeString(product.price);
  const directPrice =
    getNumberLikeString(storePrice?.price) ||
    getNumberLikeString(product.original_price) ||
    getNumberLikeString(product.market_price) ||
    directSellPrice;

  return {
    price: directPrice,
    sellPrice: directSellPrice,
  };
}

export function isMarketingFlashSaleSoldOutProduct(product: MarketingFlashSaleProductItem) {
  const status = getFiniteNumber(product.status);
  const stock = getFiniteNumber(product.stock);

  return (
    (status !== undefined && status !== 1) ||
    (stock !== undefined && stock <= 0) ||
    String(product.in_stock) === '2'
  );
}

export function isMarketingProductSoldOut(product: Record<string, unknown>) {
  const status = getFiniteNumber(product.status);
  const stock = getFiniteNumber(product.stock);

  if (status !== undefined && status !== 1) {
    return true;
  }

  if (stock !== undefined && stock <= 0) {
    return true;
  }

  return String(product.in_stock) === '2';
}

export function getMarketingRankingSoldText(
  product: MarketingProductItem,
  formatSoldText: (count: string) => string,
) {
  const configuredText =
    getStringValue(product.sold_text) ||
    getStringValue(product.sales_text) ||
    getStringValue(product.rank_text) ||
    getStringValue(product.hot_text);
  if (configuredText) {
    return configuredText;
  }

  const soldCount = Number(
    product.sold_count ?? product.sales_count ?? product.sale_count ?? product.sold_num,
  );
  if (Number.isFinite(soldCount) && soldCount > 0) {
    const count =
      soldCount >= 1000
        ? `${(soldCount / 1000).toFixed(soldCount >= 10000 ? 0 : 1)}K`
        : String(soldCount);
    return formatSoldText(count);
  }

  return '';
}

export type MarketingRankingProductCardView = Pick<
  MarketingRankingProductCardProps,
  'href' | 'image' | 'hoverImage' | 'imageAlt' | 'name' | 'priceText' | 'originalPriceText'
>;

export function getMarketingRankingProductCardView(
  product: MarketingProductItem,
  currency: unknown,
  userInfo: unknown,
  imageHoverRule?: unknown[],
): MarketingRankingProductCardView {
  const image = getMarketingFlashSaleProductImage(product);
  const hoverImage = getMarketingProductHoverImage(product, imageHoverRule);
  const name = getMarketingFlashSaleProductName(product);
  const href = getMarketingFlashSaleProductHref(product);
  const { price, sellPrice } = getMarketingFlashSaleDisplayPrice(product, currency, userInfo);
  const priceText = sellPrice ? formatPriceNumberWithSymbol(sellPrice, currency) : '';
  const originalPriceText = price ? formatPriceNumberWithSymbol(price, currency) : '';

  return {
    href: href || undefined,
    image: image || undefined,
    hoverImage: hoverImage || undefined,
    imageAlt: name,
    name,
    priceText,
    originalPriceText:
      originalPriceText && originalPriceText !== priceText ? originalPriceText : undefined,
  };
}
